import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import "../css/OrderReview.css";
import {
  fruitBaskets,
  getProduct,
  images,
  initialAddresses,
  products,
  serviceAreas,
  shakeProducts,
  timeSlots,
  weekdays,
} from "../data";
import { useApp } from "../store";
import { nextDeliveryDate } from "../utils/admin";
import {
  AddressCard,
  Brand,
  DeliveryCard,
  EmptyState,
  PageHeader,
  PrimaryButton,
  ProductCard,
  StatusBadge,
} from "../components/UI";

export function OrderReview() {
  const {
    draft,
    addresses,
    saveSchedule,
    saveOrder,
    updateOrderAndSchedule,
    updateConcreteOrder,
    user,
    setDraft,
  } = useApp();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const editScheduleId =
    draft?.editScheduleId || queryParams.get("editScheduleId");
  const editOrderId = draft?.editOrderId || queryParams.get("editOrderId");
  const editMode = draft?.editMode || queryParams.get("editMode");
  const navigate = useNavigate();
  const product = getProduct(draft?.productId) || shakeProducts[0];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!user?.uid)
      navigate(
        `/register?redirect=${encodeURIComponent(`/order-review${location.search}`)}`,
        {
          replace: true,
        },
      );
  }, [user?.uid, navigate]);
  const address =
    addresses.find((a) => a.id === draft?.addressId) || addresses[0];
  const confirm = async () => {
    if (!user?.uid) {
      navigate(`/register?redirect=${encodeURIComponent("/order-review")}`);
      return;
    }
    if (!address?.id) {
      alert("Please select a delivery address before confirming.");
      navigate("/addresses");
      return;
    }

    if (editMode === "order" && editOrderId) {
      try {
        const updated = await updateConcreteOrder(editOrderId, {
          deliveryDate: draft?.startDate,
          timeSlot: draft?.deliveryTimeSlot || timeSlots[2],
          deliveryTimeSlot: draft?.deliveryTimeSlot || timeSlots[2],
          addressId: address.id,
          quantity: draft?.quantity || 1,
          amount: product.price * (draft?.quantity || 1),
          addressLine: address?.line || "",
          addressArea: address?.area || "",
          addressPincode: address?.pincode || "",
          frequency: draft?.frequency || "One Time",
          recurring: draft?.frequency !== "One Time",
          days: draft?.days || [],
          ...(product.type === "fruit_basket"
            ? { selectedFoods: draft?.selectedFoods || [] }
            : {}),
        });
        setDraft(null);
        navigate(`/deliveries/${updated.id}`, {
          replace: true,
          state: { orderUpdated: true },
        });
      } catch (err) {
        alert(err?.message || "Unable to update this delivery.");
      }
      return;
    }

    if (editScheduleId) {
      try {
        const result = await updateOrderAndSchedule(editScheduleId, {
          productId: product.id,
          type: product.type,
          frequency: draft?.frequency,
          days: draft?.days || [],
          startDate: draft?.startDate,
          deliveryTimeSlot: draft?.deliveryTimeSlot || timeSlots[2],
          addressId: address.id,
          quantity: draft?.quantity || 1,
          ...(product.type === "fruit_basket"
            ? { selectedFoods: draft?.selectedFoods || [] }
            : {}),
        });
        setDraft(null);
        navigate(editOrderId ? `/deliveries/${editOrderId}` : "/deliveries", {
          replace: true,
          state: {
            scheduleUpdated: true,
            updatedOrders: result?.updatedOrders || 0,
            lockedOrders: result?.lockedOrders || 0,
          },
        });
      } catch (err) {
        alert(err?.message || "Unable to update the existing schedule.");
      }
      return;
    }

    const deliveryDate =
      nextDeliveryDate(
        {
          frequency: draft?.frequency || "One Time",
          days: draft?.days || [],
          startDate: draft?.startDate || new Date().toISOString().slice(0, 10),
          status: "active",
        },
        new Date(),
      ) ||
      draft?.startDate ||
      new Date().toISOString().slice(0, 10);
    const schedule = await saveSchedule({
      productId: product.id,
      type: product.type,
      frequency: draft?.frequency,
      days: draft?.days || [],
      startDate: draft?.startDate,
      deliveryTimeSlot: draft?.deliveryTimeSlot,
      addressId: address?.id,
      quantity: draft?.quantity || 1,
      ...(product.type === "fruit_basket"
        ? { selectedFoods: draft?.selectedFoods || [] }
        : {}),
      status: "active",
    });
    const displayDate = new Date(`${deliveryDate}T00:00:00`);
    const formattedDate = displayDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    });
    const formattedFullDate = displayDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const delivery = {
      productId: product.id,
      date: formattedDate,
      fullDate: formattedFullDate,
      timeSlot: draft?.deliveryTimeSlot || timeSlots[2],
      addressId: address?.id,
      quantity: draft?.quantity || 1,
      amount: product.price * (draft?.quantity || 1),
      status: "Scheduled",
      recurring: draft?.frequency !== "One Time",
      days: draft?.days || [],
      ...(product.type === "fruit_basket"
        ? { selectedFoods: draft?.selectedFoods || [] }
        : {}),
      scheduleId: schedule.id,
    };
    const savedOrder = await saveOrder({
      ...delivery,
      userId: user.uid,
      scheduleId: schedule.id,
      deliveryDate,
      productName: product.name,
      type: product.type,
      frequency: draft?.frequency || "One Time",
      customerName: user.name || "",
      customerMobile: user.mobile || "",
      timeSlot: draft?.deliveryTimeSlot || timeSlots[2],
      addressLine: address?.line || "",
      addressArea: address?.area || "",
      addressPincode: address?.pincode || "",
    });
    setDraft(null);
    navigate("/order-success", {
      state: {
        delivery: { ...delivery, id: savedOrder.id },
        product,
        address,
        deliveryDate,
      },
    });
  };
  return (
    <div className="flow-page">
      <header className="flow-header">
        <Link
          to={
            editMode === "order" && editOrderId
              ? `/schedule/${product.type}/${product.id}?editOrderId=${encodeURIComponent(editOrderId)}&editMode=order`
              : editScheduleId
                ? `/schedule/${product.type}/${product.id}?editScheduleId=${encodeURIComponent(editScheduleId)}${editOrderId ? `&editOrderId=${encodeURIComponent(editOrderId)}` : ""}`
                : `/schedule/${product.type}/${product.id}`
          }
          className="icon-button"
        >
          <i className="fa-solid fa-arrow-left" />
        </Link>
        <Brand />
        <span className="step-label">Step 2 of 2</span>
      </header>
      <div className="container flow-container narrow">
        <div className="flow-progress">
          <span className="active" />
          <span className="active" />
        </div>
        <PageHeader
          back={false}
          title="Review your schedule"
          copy={
            editScheduleId
              ? "Review the changes to your existing schedule. No new order will be created."
              : "Make sure everything looks right."
          }
        />
        {(editScheduleId || editMode === "order") && (
          <div className="schedule-edit-notice mb-3">
            <i className="fa-solid fa-clock" />
            <div>
              <strong>
                Changes allowed only more than 24 hours before delivery.
              </strong>
              <span>
                {editMode === "order"
                  ? "Only this delivery will be updated; other recurring deliveries stay unchanged."
                  : "Any delivery already inside the 24-hour window will stay unchanged."}
              </span>
            </div>
          </div>
        )}
        <div className="review-card">
          <div className="review-product">
            <img src={product.image} alt="" />
            <div>
              <span className="card-kicker">Product</span>
              <h2>{product.name}</h2>
              <p>Quantity: {draft?.quantity || 1}</p>
            </div>
            <strong>₹{product.price}</strong>
          </div>
          {product.type === "fruit_basket" && (
            <div className="review-row">
              <i className="fa-solid fa-basket-shopping" />
              <div>
                <span>Selected fruits</span>
                <strong>
                  {(draft?.selectedFoods?.length
                    ? draft.selectedFoods
                    : [product.ingredients[0]]
                  ).join(", ")}
                </strong>
              </div>
              <Link to={`/fruits/${product.id}`}>Edit</Link>
            </div>
          )}
          <div className="review-row">
            <i className="fa-regular fa-calendar" />
            <div>
              <span>Schedule</span>
              <strong>{draft?.frequency || "3 Days a Week"}</strong>
              <p>
                {draft?.days?.length
                  ? draft.days.join(", ")
                  : "One-time delivery"}{" "}
                · Starting {draft?.startDate || "—"}
              </p>
            </div>
            <Link
              to={
                editMode === "order" && editOrderId
                  ? `/schedule/${product.type}/${product.id}?editOrderId=${encodeURIComponent(editOrderId)}&editMode=order`
                  : editScheduleId
                    ? `/schedule/${product.type}/${product.id}?editScheduleId=${encodeURIComponent(editScheduleId)}${editOrderId ? `&editOrderId=${encodeURIComponent(editOrderId)}` : ""}`
                    : `/schedule/${product.type}/${product.id}`
              }
            >
              Edit
            </Link>
          </div>
          <div className="review-row">
            <i className="fa-regular fa-clock" />
            <div>
              <span>Preferred window</span>
              <strong>{draft?.deliveryTimeSlot || "7:30 AM – 8:00 AM"}</strong>
            </div>
          </div>
          <div className="review-row">
            <i className="fa-solid fa-location-dot" />
            <div>
              <span>Delivery address</span>
              <strong>{address?.type}</strong>
              <p>
                {address?.line}, {address?.area}, Pune
              </p>
            </div>
            <Link to="/addresses">Edit</Link>
          </div>
          <div className="price-summary">
            <div>
              <span>Estimated price per delivery</span>
              <strong>₹{product.price}</strong>
            </div>
            {draft?.frequency !== "One Time" && (
              <div>
                <span>Estimated weekly</span>
                <strong>
                  ₹{product.price * Math.max(1, draft?.days?.length || 3)}
                </strong>
              </div>
            )}
            <small>
              Payment setup coming soon. Will collect the payment while delivery
              for Phase 1.
            </small>
          </div>
        </div>
        <div className="fresh-note">
          <i className="fa-solid fa-leaf" />
          <div>
            <strong>
              {product.type === "protein_shake"
                ? "Your shake will be prepared fresh for your scheduled delivery."
                : "Fruit availability may vary by season and market conditions. If any selected fruit is unavailable, we'll increase the quantity of the available fruits in your basket."}
            </strong>
            <p>
              {product.type === "fruit_basket" &&
                "We don’t store fruits. We buy them fresh from the market based on scheduled orders and deliver them fresh to your doorstep."}
            </p>
          </div>
        </div>
        <PrimaryButton className="w-100 btn-lg" onClick={confirm}>
          {editMode === "order"
            ? "Update this delivery"
            : editScheduleId
              ? "Update existing schedule"
              : "Confirm schedule"}
        </PrimaryButton>
      </div>
    </div>
  );
}

export default OrderReview;
